import React, { useState } from "react";
import { Segmented, Card, Spin, Empty } from "antd";
import {
  TpmBar,
  TpmLine,
  TpmDonut,
} from "../../pages/home/chartKit";
import { HOME_SERIES } from "../../pages/home/homeTheme";
import "./dynamicChart.scss";

/**
 * One dataset, four ways of looking at it — used by the TPM summary cards.
 *
 * Styling comes from the shared chart kit (muted palette, value labels on every
 * mark, no chart junk), so this file only decides the shape of each variant and
 * the card around it. The chart-type picker is a small segmented control rather
 * than a 130px dropdown: it is a view switch, not data entry.
 */
const CHART_TYPES = [
  { label: "Donut", value: "pie" },
  { label: "Bar", value: "bar" },
  { label: "Line", value: "line" },
  { label: "Area", value: "area" },
];

const DynamicChart = ({
  title,
  icon,
  loading,
  data,
  colors = HOME_SERIES,
  defaultChartType = "pie",
}) => {
  const [chartType, setChartType] = useState(defaultChartType);

  if (!data || !data.datasets || data.datasets[0].data.every((v) => v === 0)) {
    return (
      <Card className="tpmSummaryCollectionGoal w-100">
        <Empty description="No data available" />
      </Card>
    );
  }

  const palette = colors?.length ? colors : HOME_SERIES;

  // One dataset per category, so each keeps its own colour and its own legend
  // entry on the bar variant.
  const perCategory = () =>
    data.labels.map((label, i) => ({
      label,
      data: [data.datasets[0].data[i]],
      backgroundColor: palette[i % palette.length],
      borderColor: palette[i % palette.length],
      borderWidth: 0,
      borderRadius: 4,
      maxBarThickness: 18,
    }));

  const renderChart = () => {
    switch (chartType) {
      case "bar":
        return (
          <TpmBar
            data={{ labels: [" "], datasets: perCategory() }}
            options={{ indexAxis: "y", maintainAspectRatio: false }}
          />
        );

      case "line":
      case "area":
        return (
          <TpmLine
            data={{
              labels: data.labels,
              datasets: [
                {
                  label: title || "Values",
                  data: data.datasets[0].data,
                  fill: chartType === "area",
                  backgroundColor: `${palette[0]}22`,
                  borderColor: palette[0],
                  pointBackgroundColor: palette[0],
                  pointRadius: 3,
                  borderWidth: 2,
                  tension: 0.35,
                },
              ],
            }}
            options={{
              maintainAspectRatio: false,
              plugins: { legend: { display: false } },
            }}
          />
        );

      default:
        return (
          <TpmDonut
            data={{
              labels: data.labels,
              datasets: [
                {
                  ...data.datasets[0],
                  backgroundColor: data.labels.map(
                    (_, i) => palette[i % palette.length]
                  ),
                  borderWidth: 0,
                  spacing: 2,
                },
              ],
            }}
            options={{ cutout: "72%", maintainAspectRatio: false }}
          />
        );
    }
  };

  return (
    <Card
      className="tpmSummaryCollectionGoal w-100"
      title={
        <div className="dynamicChartHead">
          <span className="dynamicChartTitle">
            {icon} {title}
          </span>
          <Segmented
            size="small"
            value={chartType}
            onChange={setChartType}
            options={CHART_TYPES}
          />
        </div>
      }
    >
      <div className="dynamicChartBody">{loading ? <Spin /> : renderChart()}</div>
    </Card>
  );
};

export default DynamicChart;
